'use client'

import { useEffect, useState } from 'react'

// MSW init starts when this client bundle loads — before any React component
// mounts. The dynamic import keeps msw/browser code-split. Evaluates to `null`
// in production, allowing bundlers to tree-shake all downstream references.
const mswReady =
  process.env.NODE_ENV === 'development'
    ? import('./init-msw').then(({ initMsw }) => initMsw())
    : null

// Caches the resolved init result so that HMR/fast-refresh re-renders can
// synchronously read it in the `useState` initializer, avoiding a null flash.
let cachedResult: { mswEnabled: boolean } | null = null
if (process.env.NODE_ENV === 'development') {
  mswReady
    ?.then((result) => {
      cachedResult = result
    })
    .catch(() => {
      cachedResult = { mswEnabled: false }
    })
}

function MswDevGate({
  children,
}: {
  children: React.ReactNode
}): React.ReactNode {
  const [state, setState] = useState<{ ready: boolean; mswEnabled: boolean }>(
    () =>
      // If already resolved (HMR / fast-refresh), skip the null flash
      cachedResult
        ? { ready: true, mswEnabled: cachedResult.mswEnabled }
        : { ready: false, mswEnabled: false },
  )

  useEffect(() => {
    if (cachedResult) return // Already handled in initial state
    mswReady
      ?.then(({ mswEnabled }) => setState({ ready: true, mswEnabled }))
      .catch(() => setState({ ready: true, mswEnabled: false }))
  }, [])

  if (!state.ready) {
    return null
  }

  return children
}

export function MswInit({
  children,
}: {
  children: React.ReactNode
}): React.ReactNode {
  if (process.env.NODE_ENV !== 'development') {
    return children // Compile-time constant — bundler strips MswDevGate entirely
  }

  return <MswDevGate>{children}</MswDevGate>
}
